// Download v2 — public install page: app-icon header, a red SmartScreen heads-up
// (above the downloads so it's read first), one big Windows x64 card that already
// bundles the panel, the optional Premiere panel card, and a system-requirements strip.
// Version + links come from /api/latest (the site Worker reads the updater feed
// server-side, since beta.yml has no CORS), with a hardcoded fallback so the
// buttons always work.
import { useEffect, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon, IconTile } from '../components/icons';
import { SiteNav } from '../sections/hero';
import { BigFooter, DISCORD_INVITE } from '../sections/footer';
import { usePageTitle } from './shell';
import logoMark from '../assets/logo-mark.svg';
import { track } from '../track';

const UPDATES = 'https://updates.getclipradar.com';
// The panel file carries no version in its name, so this URL cannot go stale.
// There is deliberately no pinned installer URL: R2 keeps only the newest 3
// releases, so any hardcoded version 404s within three ships while the button
// still looks fine. If we cannot read the real version we say so instead.
const CCX = `${UPDATES}/co.clipradar.premiere_premierepro.ccx`;

// the primary card's shell — shared by the ready / loading / unavailable states
// so the page does not reflow as the version resolves
const DL_SHELL: CSSProperties = {
  textDecoration: 'none', display: 'flex', flexDirection: 'column', borderRadius: 18, padding: 1,
  background: 'linear-gradient(170deg, rgba(74,227,255,0.55), rgba(255,255,255,0.08) 38%, rgba(139,124,255,0.45) 88%)',
  boxShadow: '0 24px 70px rgba(0,0,0,0.5), 0 0 60px rgba(74,227,255,0.10)',
};
const DL_SHELL_QUIET: CSSProperties = {
  ...DL_SHELL,
  background: 'linear-gradient(170deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05) 60%)',
  boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
};
const DL_CARD: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 14, flex: 1, borderRadius: 17,
  padding: '24px 24px 22px', background: 'rgba(8,8,10,0.96)',
};
interface LatestInfo {
  version: string;
  exe: string;
  ccx: string;
}

const REQS: { icon: 'monitor' | 'cpu' | 'film'; t: string; d: string }[] = [
  { icon: 'monitor', t: 'Windows 10 / 11', d: '64-bit' },
  { icon: 'cpu', t: 'NVIDIA GPU (CUDA)', d: 'required · ~6 GB VRAM' },
  { icon: 'film', t: 'Premiere Pro 25.6+', d: 'optional, for the panel' },
];

export function DownloadPage() {
  usePageTitle('Download');
  const [info, setInfo] = useState<LatestInfo | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  // download-started acknowledgment — the click otherwise gives zero feedback
  // (the browser's download bar is easy to miss). One echo ring on the card's
  // icon + a status line under the cards; repeat clicks re-fire via the counter.
  const [started, setStarted] = useState<{ what: 'exe' | 'ccx'; n: number } | null>(null);
  const markStarted = (what: 'exe' | 'ccx') => {
    track('download_click', what);
    setStarted((s) => ({ what, n: (s?.n ?? 0) + 1 }));
  };

  useEffect(() => {
    let live = true;
    setStatus('loading');
    fetch('/api/latest')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        if (!live) return;
        if (data && typeof data.version === 'string' && typeof data.exe === 'string') {
          setInfo({ version: data.version, exe: data.exe, ccx: typeof data.ccx === 'string' ? data.ccx : CCX });
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        if (live) setStatus('error');
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden', padding: '148px 24px 96px' }}>
        {/* atmosphere */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, black, transparent 520px)', WebkitMaskImage: 'linear-gradient(180deg, black, transparent 520px)' }}></div>
          <div style={{ position: 'absolute', top: -260, left: '50%', transform: 'translateX(-50%)', width: 900, height: 520, background: 'radial-gradient(ellipse at center, rgba(74,227,255,0.09), rgba(139,124,255,0.04) 50%, transparent 70%)', filter: 'blur(30px)' }}></div>
        </div>

        <div className="crw-page-in" style={{ position: 'relative', maxWidth: 880, margin: '0 auto' }}>
          {/* ---- header: app icon + title ---- */}
          <header style={{ display: 'flex', alignItems: 'center', gap: 22, marginBottom: 42, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', padding: 1, borderRadius: 23, flex: 'none', background: 'linear-gradient(160deg, rgba(74,227,255,0.45), rgba(255,255,255,0.08) 40%, rgba(139,124,255,0.4))', boxShadow: '0 14px 40px rgba(0,0,0,0.5), 0 0 44px rgba(74,227,255,0.12)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 82, height: 82, borderRadius: 22, background: 'linear-gradient(180deg, rgba(18,18,23,0.98), rgba(9,9,12,0.99))' }}>
                <img src={logoMark} width={46} height={46} alt="" />
              </span>
            </span>
            <span>
              <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(30px, 4.4vw, 44px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.1 }}>
                Download ClipRadar
              </h1>
              <p style={{ margin: '8px 0 0', fontSize: 16, lineHeight: 1.6, color: 'var(--fg-2)' }}>Available for Windows.</p>
            </span>
          </header>

          {/* ---- SmartScreen heads-up — red + above the downloads so it's read first ---- */}
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '16px 18px', marginBottom: 30, borderRadius: 14, background: 'var(--danger-dim)', border: '1px solid rgba(255,84,104,0.42)', boxShadow: '0 0 34px rgba(255,84,104,0.09)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 11, flex: 'none', color: 'var(--danger)', background: 'rgba(255,84,104,0.13)', border: '1px solid rgba(255,84,104,0.32)' }}>
              <CRWIcon d="shield" size={20} />
            </span>
            <span style={{ minWidth: 0 }}>
              <b style={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'var(--fg-1)', marginBottom: 4 }}>
                Windows will warn you when you run it — that’s expected, and ClipRadar is safe.
              </b>
              <span style={{ display: 'block', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-2)' }}>
                SmartScreen shows an “unknown publisher” notice because the beta isn’t code-signed yet. Click{' '}
                <strong style={{ color: 'var(--fg-1)' }}>More info → Run anyway</strong> to install. You may see it again on updates — that’s normal, not a problem with your download.
              </span>
            </span>
          </div>

          {/* ---- latest version cards ---- */}
          <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)', marginBottom: 14 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
            Latest version
          </span>
          <div className="crw-dl-grid" style={{ display: 'grid', gridTemplateColumns: '1.5fr 0.85fr', gap: 14, alignItems: 'stretch' }}>
            {/* Windows x64 — the primary card */}
            {status === 'ready' && info ? (
              <a href={info.exe} download onClick={() => markStarted('exe')} className="crw-dl-cardlink" style={DL_SHELL}>
                <span className="crw-dl-card" style={DL_CARD}>
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ flex: 1 }}>
                      <b style={{ display: 'block', fontSize: 18, fontWeight: 650, color: 'var(--fg-1)' }}>Windows (x64)</b>
                      <i style={{ display: 'block', fontStyle: 'normal', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>ClipRadar v{info.version}</i>
                    </span>
                    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, color: 'var(--signal-300)', background: 'rgba(74,227,255,0.1)', border: '1px solid rgba(74,227,255,0.28)', flex: 'none' }}>
                      {started?.what === 'exe' ? <span key={started.n} className="crw-found-ring" aria-hidden="true"></span> : null}
                      <CRWIcon d={started?.what === 'exe' ? 'check' : 'download'} size={19} />
                    </span>
                  </span>
                  <span style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--fg-3)' }}>
                    Small web installer — pulls the app and its AI models on first run (~3.4&nbsp;GB). Updates land automatically after that.
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--signal-300)' }}>
                    <CRWIcon d="check" size={14} /> Premiere Pro panel included — no separate download needed.
                  </span>
                </span>
              </a>
            ) : (
              /* No link until we know the real version. Handing out a guessed
                 URL is how this page shipped a dead download for months. */
              <div style={DL_SHELL_QUIET} aria-live="polite">
                <span className="crw-dl-card" style={DL_CARD}>
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ flex: 1 }}>
                      <b style={{ display: 'block', fontSize: 18, fontWeight: 650, color: 'var(--fg-1)' }}>Windows (x64)</b>
                      <i style={{ display: 'block', fontStyle: 'normal', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-3)', marginTop: 6 }}>
                        {status === 'loading' ? 'Checking for the latest build…' : 'Version unavailable'}
                      </i>
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, color: 'var(--fg-3)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--line-2)', flex: 'none' }}>
                      <CRWIcon d={status === 'loading' ? 'refresh' : 'ban'} size={19} />
                    </span>
                  </span>
                  {status === 'loading' ? (
                    <span style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--fg-3)' }}>
                      Looking up the current release so this link points at the right build.
                    </span>
                  ) : (
                    <>
                      <span style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--fg-3)' }}>
                        We can’t reach the release feed, so there’s no download link to give you — a guessed one would just fail. It’s usually back within a minute.
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <button type="button" className="crw-copybtn" onClick={() => setAttempt((n) => n + 1)}>
                          <CRWIcon d="refresh" size={14} /> Try again
                        </button>
                        <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 500, color: 'var(--signal-300)' }}>
                          Ask on Discord
                        </a>
                      </span>
                    </>
                  )}
                </span>
              </div>
            )}

            {/* Premiere panel — optional standalone; the app download above already bundles it */}
            <a href={info?.ccx ?? CCX} download onClick={() => markStarted('ccx')} className="crw-glass crw-gradborder crw-dl-cardlink" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: 10, borderRadius: 16, padding: '18px 18px 16px' }}>
              <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 14.5, fontWeight: 650, color: 'var(--fg-2)' }}>Premiere Pro panel</b>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-1)', background: 'rgba(255,255,255,0.08)', border: '1px solid var(--line-2)', borderRadius: 999, padding: '3px 9px' }}>Optional</span>
                  </span>
                  <i style={{ display: 'block', fontStyle: 'normal', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-4)', marginTop: 6 }}>.ccx · standalone reinstall</i>
                </span>
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 11, color: 'var(--fg-3)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--line-2)', flex: 'none' }}>
                  {started?.what === 'ccx' ? <span key={started.n} className="crw-found-ring" aria-hidden="true"></span> : null}
                  <CRWIcon d={started?.what === 'ccx' ? 'check' : 'download'} size={17} />
                </span>
              </span>
              <span style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-3)' }}>
                Already included in the app download — you don’t need this to get started. Grab it only to reinstall the panel on its own: double-click the .ccx and Creative Cloud adds it to Premiere Pro.
              </span>
            </a>
          </div>

          {/* download-started status line — polite live region living inside the
              cards→reqs gap, so its appearance never shifts the layout below */}
          <div role="status" aria-live="polite" style={{ minHeight: 38, display: 'grid', alignItems: 'center' }}>
            {started ? (
              <p key={`${started.what}-${started.n}`} className="crw-pop" style={{ margin: '0 2px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.06em', color: 'var(--signal-300)' }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
                {started.what === 'exe'
                  ? 'Download started — watch for the installer in your downloads bar.'
                  : 'Panel download started — double-click the .ccx and Creative Cloud installs it.'}
              </p>
            ) : null}
          </div>

          {/* ---- system requirements strip ---- */}
          <div className="crw-dl-reqs" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, borderRadius: 14, border: '1px solid var(--line-1)', background: 'var(--surface-card)', overflow: 'hidden' }}>
            {REQS.map((r, i) => (
              <div key={r.t} className="crw-dl-req" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 18px', borderLeft: i ? '1px solid var(--line-1)' : 'none' }}>
                <IconTile d={r.icon} size={34} />
                <span>
                  <b style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>{r.t}</b>
                  <i style={{ display: 'block', fontStyle: 'normal', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 1 }}>{r.d}</i>
                </span>
              </div>
            ))}
          </div>

          {/* ---- license pointer ---- */}
          <p style={{ margin: '22px 0 0', fontSize: 13.5, lineHeight: 1.7, color: 'var(--fg-3)' }}>
            On the License step, paste the key from your email and hit Activate (needs internet once). No key yet?{' '}
            <Link to="/trial" className="crw-textlink" style={{ color: 'var(--signal-400)', textDecoration: 'none' }}>Start your free trial →</Link>
          </p>
        </div>
      </main>
      <BigFooter />
    </div>
  );
}
